import React from 'react';
import { Card, CardBody, Row, Col, Badge } from 'reactstrap';
import moment from 'moment';
import styles from '../../styles/public/league-registration.module.css';

const LeagueScheduleSelector = ({ schedules, selectedSchedule, onSelectSchedule }) => {
    if (!schedules || schedules.length === 0) {
        return (
            <div className="text-center py-4">
                <p className="text-muted">No schedules are currently available for this league.</p>
            </div>
        );
    }

    return (
        <Row className="g-3">
            {schedules.map((schedule) => {
                const isSelected = selectedSchedule?.id === schedule.id;
                const isFull = schedule.is_full;
                
                return (
                    <Col xs={12} md={6} key={schedule.id}>
                        <Card 
                            className={`${styles.scheduleCard} ${isSelected ? styles.selected : ''} ${isFull ? styles.disabled : ''}`}
                            onClick={() => !isFull && onSelectSchedule(schedule)}
                            style={{ cursor: isFull ? 'not-allowed' : 'pointer', opacity: isFull ? 0.6 : 1 }}
                        >
                            <CardBody>
                                <div className="d-flex justify-content-between align-items-start mb-2">
                                    <h5 className="mb-0">{schedule.title}</h5>
                                    <div className="d-flex gap-2">
                                        {isFull && (
                                            <Badge color="danger">
                                                <i className="fas fa-ban me-1"></i> Full
                                            </Badge>
                                        )}
                                        {isSelected && (
                                            <Badge color="success">
                                                <i className="fas fa-check me-1"></i> Selected
                                            </Badge>
                                        )}
                                    </div>
                                </div>
                                
                                {schedule.description && (
                                    <p className="text-muted small mb-3">
                                        {schedule.description}
                                    </p>
                                )}

                                <div className={styles.scheduleDetails}>
                                    {schedule.day_of_week && (
                                        <div className="mb-2">
                                            <i className="fas fa-calendar-day me-2 text-primary"></i>
                                            <strong>Day:</strong> {getDayName(schedule.day_of_week)}
                                        </div>
                                    )}
                                    
                                    {schedule.start_time && (
                                        <div className="mb-2">
                                            <i className="fas fa-clock me-2 text-primary"></i>
                                            <strong>Time:</strong> {formatTime(schedule.start_time)}
                                            {schedule.end_time && ` - ${formatTime(schedule.end_time)}`}
                                        </div>
                                    )}

                                    {schedule.start_date && (
                                        <div className="mb-2">
                                            <i className="fas fa-calendar-alt me-2 text-primary"></i>
                                            <strong>Starts:</strong> {moment(schedule.start_date).format('MMM D, YYYY')}
                                        </div>
                                    )}

                                    {schedule.duration_minutes && (
                                        <div className="mb-2">
                                            <i className="fas fa-hourglass-half me-2 text-primary"></i>
                                            <strong>Duration:</strong> {schedule.duration_minutes} minutes
                                        </div>
                                    )}

                                    {schedule.timeslots && schedule.timeslots.length > 0 && (
                                        <div className="mt-3 pt-2 border-top">
                                            <div className="ps-4">
                                                {schedule.timeslots.map((timeslot, index) => (
                                                    <div key={timeslot.id} className="small text-muted mb-1">
                                                        <i className="fas fa-circle me-2" style={{ fontSize: '0.4rem' }}></i>
                                                        {timeslot.start} - {timeslot.end}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                );
            })}
        </Row>
    );
};

// Helper function to get day name from number (0 = Monday)
const getDayName = (dayNum) => {
    const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    return days[dayNum] || 'N/A';
};

// Helper function to format time (HH:MM:SS to h:mm A)
const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    const [hours, minutes] = timeString.split(':');
    const date = new Date();
    date.setHours(parseInt(hours, 10));
    date.setMinutes(parseInt(minutes, 10));
    return moment(date).format('h:mm A');
};

export default LeagueScheduleSelector;
